import { isDevelopment, isTest } from "js/config";
import Api from "js/core/api";
import getLocalStorage from "js/core/utilities/localStorage";

const localStorage = getLocalStorage();

/**
 * A Simple Logger interface that we can implement with a real lib later.
 */
const types = {
    EDITOR: "editor",
    UNDO: "undo",
    CANVAS: "canvas",
    COLLABORATION: "collaboration",
    LAYOUT: "layout",
    ASSET: "asset",
    THUMBNAIL: "thumbnail",
    LOGO: "logo",
    PLAYER: "player",
    PRESENTATION: "presentation",
    SLIDE: "slide",
    USER: "user",
    ADAPTER: "adapter",
    ANIMATION: "animation",
    SELECTION: "selection",
    TASK: "task",
    AUTH: "auth",
    AUTHORING: "authoring",
    MIGRATION: "migration",
    SPELLCHECK: "spellcheck",
    NOTIFICATION: "notification",
    AUDIT: "audit",
    ANALYTICS: "analytics",
    EXPORT: "export",
    DATA_SERVICE: "dataService",
    CONVERT_TO_AUTHORING: "convertToAuthoring",
    OBSERVABLES: "observables",
    COMMENTS: "comments",
    PUSHER: "pusher",
    EXPORT_CACHE: "exportCache",
    DATA_SOURCE: "dataSource",
};

const Logger = Object.assign({
    filterLogs: [],
    filter: function(type, element) {
        if (type == undefined && element == undefined) {
            this.clearFilter();
            return;
        }
        this.filterLogs.push(type);
        localStorage.setItem("logFilter", JSON.stringify(this.filterLogs));

        if (element) {
            this.elementType = element;
        } else {
            this.elementType = "";
        }
        localStorage.setItem("elementFilter", this.elementType);
    },

    _log: function(level, type, elementType) {
        if (!Object.values(types).includes(type)) {
            console.log(type);
        }
        if (Logger.showLog(type, elementType)) {
            if (Logger.formatOutput) {
                return Function.prototype.bind.call(console[level], console, "%c" + type, "font-size: 9px; color: white; padding: 2px 4px; background: #999; text-transform: uppercase");
            } else {
                return Function.prototype.bind.call(console[level], console, "[" + type + "]");
            }
        } else {
            return () => {
            };
        }
    },

    enableAll: function() {
        this.filterLogs = Object.values(types);
        localStorage.setItem("logFilter", JSON.stringify(this.filterLogs));
    },

    clearFilter: function() {
        this.filterLogs = [];
        localStorage.setItem("logFilter", JSON.stringify(this.filterLogs));
    },

    showLog: function(type, elementType) {
        if (isTest) return true;
        if (type && !this.filterLogs.includes(type)) return false;
        if (elementType && (this.elementType != "" && this.elementType != elementType)) return false;
        return true;
    },

    debug: function(type, elementType) {
        if (isDevelopment) {
            return Logger._log("info", type, elementType);
        } else {
            return () => { };
        }
    },

    info: function(type, elementType) {
        return Logger._log("info", type, elementType);
    },

    group: function(type, elementType) {
        return Logger._log("groupCollapsed", type, elementType);
    },

    groupEnd: function(type, elementType) {
        return Logger._log("groupEnd", type, elementType);
    },

    warn: function(message) {
        window.console.warn(message);
    },

    error: function(error) {
        if (error == null) {
            console.warn("error is not defined or null");
            return;
        }

        const ignoreErrors = ["popup_closed_by_user"];
        if (ignoreErrors.includes(error.error)) return;

        if (error.muteError) {
            console.warn(error);
            return;
        }
        if (typeof error === "string") {
            error = new Error(error);
        }
        if (!isError(error)) {
            try {
                error = new Error(JSON.stringify(error));
            } catch (e) {
                //log this error instead as we are passing in invalid data to the logger.
                error = e;
            }
        }
        if (window.Raven) {
            window.Raven.captureException(error);
        } else {
            console.error(error);
        }
    },

    audit: function(namespace, action, properties) {
        if (!navigator.onLine) {
            return;
        }

        if (isDevelopment) {
            Logger._log("info", types.AUDIT)(namespace, action, properties);
        }

        Api.audit.post({
            namespace: namespace,
            action: action,
            props: properties
        }).catch(err => {
            //VOID
        });
    }
}, types);

Logger.filterLogs = JSON.parse(localStorage.getItem("logFilter") || JSON.stringify([types.UNDO, types.COLLABORATION, types.EDITOR, types.AUTH, types.DATA_SERVICE, types.OBSERVABLES, types.COLLABORATION, types.PUSHER]));
Logger.elementType = localStorage.getItem("elementFilter");
Logger.formatOutput = localStorage.getItem("logFormatOutput") === "true";

class MutedError extends Error {
    constructor(message) {
        super(message);
        this.muteError = true;
        this.name = "MutedError";
    }
}

Logger.MutedError = MutedError;

// cross-window safe `instanceof Error` check
function isError(obj) {
    return obj && obj.stack && obj.message;
}

export default Logger;

window.log = Logger;
